import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Club, Member, Visibility } from "@pagemates/shared";
import { api, describeError } from "../../lib/api";
import type { PickPolicy } from "@pagemates/shared";
import { Avatar, Confirm, Spinner } from "../../components/ui";
import { PencilIcon } from "../../components/Icons";
import { useClub } from "./ClubLayout";
import { BackBar } from "../../components/ui";
import { GenrePicker } from "../../components/ClubHome";
import { MuteClub } from "../../components/NotificationSettings";
import { useClubBooks, useMe, useMembers } from "../../lib/queries";
import { club } from "@pagemates/shared/copy";

const txt = club.settings;

/** One group of settings: its title and a line on what it covers, then its card. Desktop puts the title in a left column. */
export function Group({ title, about, children }: { title: string; about: string; children: React.ReactNode }) {
  return (
    <section className="sgroup">
      <header><h2>{title}</h2><p>{about}</p></header>
      <div className="card stack">{children}</div>
    </section>
  );
}

/** Admin settings, off the Club tab behind the gear. Members see only "Your membership". */
export function ClubSettingsPage() {
  const { clubId, club } = useClub();
  const c = club.data!;
  const me = useMe();
  const members = useMembers(clubId);
  const isAdmin = c.myRole !== "member";
  return (
    <main className="page stack settings">
      <BackBar label={txt.club} link={{ to: "/clubs/$clubId/club", params: { clubId } }} />
      <h1>{txt.settings}</h1>
      <div className="sgroups">
        {isAdmin && <Settings club={c} />}
        {members.isPending ? <Spinner /> : <Membership club={c} members={members.data ?? []} meId={me.data?.id} />}
      </div>
    </main>
  );
}

function Settings({ club }: { club: Club }) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (patch: Parameters<typeof api.updateClub>[1]) => api.updateClub(club.id, patch),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["club"] }), qc.invalidateQueries({ queryKey: ["me"] })]),
  });
  // Everything saves as you change it; whatever just saved says so beside its name for a moment.
  const [saved, setSaved] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const put = (key: string, patch: Parameters<typeof api.updateClub>[1]) => save.mutate(patch, {
    onSuccess: () => { setSaved(key); clearTimeout(timer.current); timer.current = setTimeout(() => setSaved(null), 1800); },
  });
  useEffect(() => () => clearTimeout(timer.current), []);
  const tick = (key: string) => <Saved on={saved === key} />;

  const s = club.settings;
  const books = useClubBooks(club.id);

  return (
    <>
      <Group title={txt.theClub} about={txt.whatPeopleSeeWhen}>
        <div className="stack">
          <EditField id="s-name" label={txt.clubName} value={club.name} max={60} required saved={tick("name")}
            onSave={(v) => put("name", { name: v })} />
          <EditField id="s-desc" label={txt.oneLineAboutThe} value={club.description} max={300} placeholder={txt.twoReadersOneLamp} saved={tick("description")}
            onSave={(v) => put("description", { description: v })} />
          <GenrePicker value={club.genres} onSave={(v) => put("genres", { genres: v })} finished={books.data?.finished ?? []} saved={tick("genres")} />
          <EditField id="s-rules" label={txt.howThisClubWorks} value={club.rules} max={2000} multiline saved={tick("rules")}
            placeholder={txt.howOftenYouMeet} hint={txt.shownOnTheClub}
            onSave={(v) => put("rules", { rules: v })} />
        </div>
        <hr className="divider" />
        {/* The link saves on its own: changing it breaks the old one. */}
        <SlugField club={club} onSave={(v) => put("slug", { slug: v })} saved={tick("slug")} />
      </Group>

      <Group title={txt.whoCanFindThis} about={txt.whoCanSeeThe}>
        <VisibilitySetting club={club} saved={tick("visibility")} onSaved={() => { setSaved("visibility"); clearTimeout(timer.current); timer.current = setTimeout(() => setSaved(null), 1800); }} />
      </Group>

      <Group title={txt.readingTogether} about={txt.howTheClubShares}>
        <div className="switch">
          <div><div className="k">{txt.usualPace}{tick("usualPace")}</div><div className="d">{txt.roughlyHowManyPages}</div></div>
          <NumberSetting id="s-pace" label={txt.usualPace} value={s.usualPace} min={1} max={500} unit="pages a day" clearable onSave={(v) => put("usualPace", { settings: { usualPace: v } })} />
        </div>
        <hr className="divider" />
        <div className="switch">
          <div><div className="k">{txt.hideSpoilersInTalk}{tick("spoilerGating")}</div><div className="d">{txt.everyCommentIsMarked}</div></div>
          <input id="s-gating" className="toggle" type="checkbox" checked={s.spoilerGating} aria-label={txt.hideSpoilersInTalk}
            onChange={(e) => put("spoilerGating", { settings: { spoilerGating: e.target.checked } })} />
        </div>
        <hr className="divider" />
        <div className="switch">
          <div><div className="k">{txt.askersCanReadAnswers}{tick("earlyReveal")}</div><div className="d">{txt.ifSomeoneAnswersYour}</div></div>
          <input id="s-reveal" className="toggle" type="checkbox" checked={s.earlyReveal} aria-label={txt.askersCanReadAnswers}
            onChange={(e) => put("earlyReveal", { settings: { earlyReveal: e.target.checked } })} />
        </div>
      </Group>

      <Group title={txt.pickingTheNextBook} about={txt.howTheClubDecides}>
        <div className="switch stacked">
          <div><div className="k">{txt.howItGetsPicked}{tick("pickPolicy")}</div><div className="d">{txt.whicheverYouChooseEveryone}</div></div>
          <div className="seg" role="group" aria-label={txt.howTheNextBook}>
            {([["admins", txt.adminsPick], ["vote", txt.vote], ["turns", txt.takeTurns]] as [PickPolicy, string][]).map(([k, label]) => (
              <button key={k} type="button" aria-pressed={s.pickPolicy === k} onClick={() => put("pickPolicy", { settings: { pickPolicy: k } })}>{label}</button>
            ))}
          </div>
          <div className="d">{{ admins: txt.anAdminChoosesThe, vote: txt.membersVoteOnThe, turns: txt.membersTakeTurnsChoosing }[s.pickPolicy]}</div>
        </div>
        {s.pickPolicy === "vote" && (
          <div className="sub-settings">
            <div className="switch">
              <div><div className="k">{txt.countedBy}{tick("voteMethod")}</div><div className="d">{txt.rankedEveryonePutsThe}</div></div>
              <div className="seg" role="group" aria-label={txt.howAVoteIs}>
                <button type="button" aria-pressed={s.voteMethod === "ranked"} onClick={() => put("voteMethod", { settings: { voteMethod: "ranked" } })}>{txt.ranked}</button>
                <button type="button" aria-pressed={s.voteMethod === "approval"} onClick={() => put("voteMethod", { settings: { voteMethod: "approval" } })}>{txt.approval}</button>
              </div>
            </div>
          </div>
        )}
        {s.pickPolicy === "turns" && <div className="sub-settings"><TurnOrder clubId={club.id} /></div>}
        {s.pickPolicy === "turns" && club.visibility === "open" && club.memberCount > 20 && (
          <p className="small notice" style={{ margin: 0 }}>{txt.withMembersTakingTurns(club.memberCount)}</p>
        )}
        <hr className="divider" />
        <div className="switch">
          <div><div className="k">{txt.theShortlistIsMade}{tick("deckFill")}</div><div className="d">{txt.theShortlistCalledOn(s.deckFill === "wanted" ? txt.rightNowItS : txt.rightNowAdminsChoose)}</div></div>
          <div className="seg" role="group" aria-label={txt.theShortlistIsMade}>
            <button type="button" aria-pressed={s.deckFill === "wanted"} onClick={() => put("deckFill", { settings: { deckFill: "wanted" } })}>{txt.mostWanted}</button>
            <button type="button" aria-pressed={s.deckFill === "admins"} onClick={() => put("deckFill", { settings: { deckFill: "admins" } })}>{txt.admins}</button>
          </div>
        </div>
        {s.deckFill === "wanted" && (
          <div className="sub-settings">
            <div className="switch">
              <div><div className="k">{txt.shortlistSize}{tick("deckSize")}</div><div className="d">{txt.howManyBooksAre}</div></div>
              <NumberSetting id="s-deck" label={txt.shortlistSize} value={s.deckSize} min={2} max={12} unit={txt.books} onSave={(v) => put("deckSize", { settings: { deckSize: v ?? 6 } })} />
            </div>
          </div>
        )}
      </Group>

      <Group title={txt.suggestions} about={txt.booksMembersPutForward}>
        <div className="switch">
          <div><div className="k">{txt.whoCanSuggest}{tick("onDeckPolicy")}</div><div className="d">{txt.whoCanAddA}</div></div>
          <div className="seg" role="group" aria-label={txt.whoCanSuggest}>
            <button type="button" aria-pressed={s.onDeckPolicy === "everyone"} onClick={() => put("onDeckPolicy", { settings: { onDeckPolicy: "everyone" } })}>{txt.everyone}</button>
            <button type="button" aria-pressed={s.onDeckPolicy === "admins"} onClick={() => put("onDeckPolicy", { settings: { onDeckPolicy: "admins" } })}>{txt.admins}</button>
          </div>
        </div>
        <hr className="divider" />
        <div className="switch">
          <div><div className="k">{txt.limitPerMember}{tick("suggestionCap")}</div><div className="d">{txt.capHowManySuggestions}</div></div>
          <span className="row" style={{ gap: 10, flex: "none" }}>
            {s.suggestionCap !== null && <NumberSetting id="s-cap" label={txt.limitPerMember} value={s.suggestionCap} min={1} max={50} unit={txt.each} onSave={(v) => put("suggestionCap", { settings: { suggestionCap: v ?? 3 } })} />}
            <input id="s-cap-on" className="toggle" type="checkbox" checked={s.suggestionCap !== null} aria-label={txt.limitSuggestionsPerMember}
              onChange={(e) => put("suggestionCap", { settings: { suggestionCap: e.target.checked ? 3 : null } })} />
          </span>
        </div>
        <hr className="divider" />
        <div className="switch">
          <div><div className="k">{txt.tidyAwayOldSuggestions}{tick("retireQuiet")}</div><div className="d">{txt.suggestionsNobodyHasShown}</div></div>
          <input id="s-retire" className="toggle" type="checkbox" checked={s.retireQuiet} aria-label={txt.tidyAwayOldSuggestions}
            onChange={(e) => put("retireQuiet", { settings: { retireQuiet: e.target.checked } })} />
        </div>
        {s.retireQuiet && (
          <div className="sub-settings">
            <div className="switch">
              <div><div className="k">{txt.alwaysKeepTheTop}{tick("retireKeep")}</div><div className="d">{txt.mostWantedHoweverOld}</div></div>
              <NumberSetting id="s-keep" label={txt.alwaysKeepTheTop} value={s.retireKeep} min={1} max={200} unit={txt.books} onSave={(v) => put("retireKeep", { settings: { retireKeep: v ?? 12 } })} />
            </div>
            <div className="switch">
              <div><div className="k">{txt.tidyTheRestAfter}{tick("retireDays")}</div><div className="d">{txt.withNobodyNewWanting}</div></div>
              <NumberSetting id="s-days" label={txt.tidyTheRestAfter} value={s.retireDays} min={14} max={730} unit={txt.days} onSave={(v) => put("retireDays", { settings: { retireDays: v ?? 90 } })} />
            </div>
          </div>
        )}
      </Group>
      {save.isError && <p className="error">{describeError(save.error)}</p>}
    </>
  );
}

/** Who picks next, and the order after them. Admins can skip whoever is up. */
function TurnOrder({ clubId }: { clubId: string }) {
  const qc = useQueryClient();
  const books = useClubBooks(clubId);
  const skip = useMutation({ mutationFn: () => api.skipTurn(clubId), onSuccess: () => qc.invalidateQueries({ queryKey: ["books", clubId] }) });
  const t = books.data?.turn;
  if (!t) return null;
  const i = t.order.findIndex((m) => m.id === t.user.id);
  const order = [...t.order.slice(i), ...t.order.slice(0, i)];
  return (
    <div className="turns">
      {order.map((m, n) => (
        <div key={m.id} className="turn">
          <span className="num">{n + 1}</span><Avatar name={m.name} image={m.image} /><span>{m.name}</span>
          {n === 0 && <span className="n now">next</span>}
          {n === 0 && <button type="button" className="linkish small" disabled={skip.isPending} onClick={() => skip.mutate()}>skip</button>}
        </div>
      ))}
      {skip.isError && <p className="error small">{describeError(skip.error)}</p>}
    </div>
  );
}

/**
 * A typed setting shown as text with a pencil. The pencil turns it into an
 * input with Save and Cancel; nothing changes until Save. Enter saves a
 * one-line field (Ctrl/⌘+Enter a long one), Escape cancels.
 */
function EditField({ id, label, value, max, multiline, required, placeholder, hint, saved, onSave }:
  { id: string; label: string; value: string; max: number; multiline?: boolean; required?: boolean; placeholder?: string; hint?: string; saved?: React.ReactNode; onSave: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [more, setMore] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => { if (!editing) setV(value); }, [value, editing]);
  const t = v.trim();
  const can = t !== value.trim() && (!required || t.length > 0);
  const commit = () => { if (can) onSave(t); setEditing(false); };
  const cancel = () => { setV(value); setEditing(false); };
  const keys = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); cancel(); }
    if (e.key === "Enter" && (!multiline || e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(); }
  };
  if (!editing) {
    return (
      <div className="field efield">
        <label htmlFor={`${id}-edit`}>{label}{saved}</label>
        <div className="eview">
          <span className="ev-text-wrap">
            <span className={`ev-text${multiline ? " multi" : ""}${multiline && !more ? " clamp" : ""}${value ? "" : " empty"}`}>{value || placeholder || txt.notSet}</span>
            {multiline && !more && value.length > 160 && <button type="button" className="linkish small" onClick={() => setMore(true)}>more</button>}
          </span>
          <button id={`${id}-edit`} type="button" className="pencil" aria-label={txt.edit(label.toLowerCase())} onClick={() => setEditing(true)}><PencilIcon width={16} height={16} /></button>
        </div>
        {hint && <span className="muted small">{hint}</span>}
      </div>
    );
  }
  return (
    <div className="field efield">
      <label htmlFor={id}>{label}</label>
      {multiline
        ? <textarea id={id} className="input" value={v} maxLength={max} rows={4} placeholder={placeholder} autoFocus onChange={(e) => setV(e.target.value)} onKeyDown={keys} />
        : <input id={id} className="input" value={v} maxLength={max} placeholder={placeholder} autoFocus onChange={(e) => setV(e.target.value)} onKeyDown={keys} />}
      <div className="row" style={{ gap: 8 }}>
        <button type="button" className="btn primary sm" disabled={!can} onClick={commit}>{txt.save}</button>
        <button type="button" className="btn ghost sm" onClick={cancel}>{txt.cancel}</button>
        {multiline && <span className="muted small" style={{ marginLeft: "auto" }}>{max - v.length}</span>}
      </div>
      {hint && <span className="muted small">{hint}</span>}
    </div>
  );
}

export const VIS_OPTIONS: { key: Visibility; label: string; line: string }[] = [
  { key: "private", label: txt.private, line: txt.onlyPeopleWithAn },
  { key: "request", label: txt.requestToJoin, line: txt.listedInTheDirectory },
  { key: "open", label: txt.open, line: txt.listedInTheDirectory2 },
];

/** The three modes as a radio list (also used by the creation flow). `disabled` greys the public ones. */
export function VisibilityChoice({ value, onChange, disabled, readOnly }: { value: Visibility; onChange: (v: Visibility) => void; disabled?: boolean; readOnly?: boolean }) {
  return (
    <div className="vis-list" role="radiogroup" aria-label={txt.whoCanFindThis}>
      {VIS_OPTIONS.map((o) => {
        const off = readOnly ? o.key !== value : disabled && o.key !== "private";
        return (
          <button key={o.key} type="button" role="radio" aria-checked={value === o.key} className={`vis-opt${value === o.key ? " on" : ""}${off ? " off" : ""}`}
            disabled={off || readOnly} onClick={() => onChange(o.key)}>
            <span className="radio-dot" aria-hidden="true" /><span><b>{o.label}</b><span className="d">{o.line}</span></span>
          </button>
        );
      })}
    </div>
  );
}

/** Settings: the owner picks the mode; each change that opens or closes the club asks first. */
function VisibilitySetting({ club, saved, onSaved }: { club: Club; saved: React.ReactNode; onSaved: () => void }) {
  const qc = useQueryClient();
  const [asking, setAsking] = useState<Visibility | null>(null);
  const owner = club.myRole === "owner";
  const noDesc = !club.description.trim();
  const waiting = club.pendingRequests ?? 0;
  const set = useMutation({
    mutationFn: (v: Visibility) => api.setVisibility(club.id, v),
    onSuccess: async () => { setAsking(null); await Promise.all([qc.invalidateQueries({ queryKey: ["club"] }), qc.invalidateQueries({ queryKey: ["join-requests", club.id] }), qc.invalidateQueries({ queryKey: ["members", club.id] })]); onSaved(); },
  });
  const choose = (v: Visibility) => {
    if (v === club.visibility) return;
    if (club.visibility === "open" && v === "request") { set.mutate(v); return; } // no effect on anyone: no question
    setAsking(v);
  };
  return (
    <>
      <div className="field"><label>{txt.mode}{saved}</label>
        <VisibilityChoice value={club.visibility} onChange={choose} disabled={noDesc} readOnly={!owner} /></div>
      {!owner ? <p className="muted small" style={{ margin: 0 }}>{txt.onlyTheOwnerCan}</p>
        : noDesc && club.visibility === "private" ? <p className="small notice" style={{ margin: 0 }}>{txt.addAOneLine}</p>
        : <p className="muted small" style={{ margin: 0 }}>{txt.inviteLinksWorkIn}</p>}
      {asking && (
        <div className="vis-confirm card" role="alertdialog" aria-label={txt.confirm}>
          {asking === "private" ? (
            <>
              <b>{txt.makePrivate(club.name)}</b>
              <p className="muted small">{txt.itLeavesTheDirectory(waiting > 0 ? txt.theWaitingForAn(waiting, waiting === 1 ? txt.person : txt.people) : "")}</p>
            </>
          ) : club.visibility === "request" && asking === "open" ? (
            <>
              <b>{txt.switchToOpen}</b>
              <p className="muted small">{txt.anyoneSignedInWill}{waiting > 0 ? <>{txt.the}<b>{waiting} {waiting === 1 ? txt.person : txt.people}</b>{txt.waitingForAnAnswer}</> : ""}</p>
            </>
          ) : (
            <>
              <b>{asking === "open" ? txt.makeOpen(club.name) : txt.listInTheDirectory(club.name)}</b>
              <p className="muted small">{txt.peopleWhoArenT(asking === "open" ? txt.itAppearsInThe : txt.itAppearsInThe2)}</p>
              <ul className="allow">
                <li>{txt.nameDescriptionAndHow}</li>
                <li>{txt.theCurrentBookAnd}</li>
                <li>{txt.usualPaceHowThe}</li>
                <li>{txt.genresWhenTheClub}</li>
                <li>{txt.theOwnerSAnd}</li>
              </ul>
              <p className="muted small">{txt.neverShownTalkThe}</p>
            </>
          )}
          {set.isError && <p className="error small">{describeError(set.error)}</p>}
          <div className="row" style={{ gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="btn ghost" onClick={() => setAsking(null)}>{txt.cancel}</button>
            <button type="button" className="btn primary" disabled={set.isPending} onClick={() => set.mutate(asking)}>
              {asking === "private" ? txt.makeItPrivate : club.visibility === "request" && asking === "open" ? (waiting > 0 ? txt.switchAndLetIn(waiting) : txt.switchToOpen2) : asking === "open" ? txt.makeItOpen : txt.listIt}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/** "✓ Saved", popping in beside a setting's name for a moment after it saves. */
export function Saved({ on }: { on: boolean }) {
  return <span className={`saved-tick${on ? " on" : ""}`} role="status" aria-live="polite">{on && <><span className="pop-check" aria-hidden="true">✓</span>{txt.saved}</>}</span>;
}

/** The club's link. Edited and saved on its own; the old link stops working. */
function SlugField({ club, onSave, saved }: { club: Club; onSave: (v: string) => void; saved?: React.ReactNode }) {
  const navigate = useNavigate();
  const [v, setV] = useState(club.slug);
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => { setV(club.slug); }, [club.slug]);
  const origin = window.location.origin.replace(/^https?:\/\//, "");
  if (!editing) {
    return (
      <div className="field"><label>{txt.link}{saved}</label>
        <div className="row small" style={{ gap: 10, flexWrap: "wrap" }}>
          <span className="muted">{txt.clubs(origin)}</span><b>{club.slug}</b>
          <button type="button" className="pencil" aria-label={txt.changeTheLink} onClick={() => setEditing(true)}><PencilIcon width={16} height={16} /></button>
          <button type="button" className={`linkish small${copied ? " copied" : " muted"}`} aria-live="polite"
            onClick={() => { void navigator.clipboard?.writeText(`${window.location.origin}/clubs/${club.slug}`).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => undefined); }}>
            {copied ? <><span className="pop-check" aria-hidden="true">✓</span>copied</> : txt.copy}</button>
        </div>
      </div>
    );
  }
  return (
    <div className="field"><label htmlFor="s-slug">{txt.link}</label>
      <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
        <span className="muted small">{txt.clubs(origin)}</span>
        <input id="s-slug" className="input" style={{ maxWidth: 220 }} value={v} maxLength={40} autoFocus
          onChange={(e) => setV(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (v !== club.slug) onSave(v); setEditing(false); } if (e.key === "Escape") setEditing(false); }} />
        <button type="button" className="btn primary sm" disabled={v === club.slug || v.length < 3} onClick={() => { onSave(v); setEditing(false); void navigate({ to: "/clubs/$clubId/club/settings", params: { clubId: v }, replace: true }); }}>{txt.save}</button>
        <button type="button" className="btn ghost sm" onClick={() => { setV(club.slug); setEditing(false); }}>{txt.cancel}</button>
      </div>
      <p className="muted small">{txt.lowercaseLettersDigitsAnd}</p>
      {club.visibility !== "private" && <p className="small notice">{txt.theClubIsPublic}</p>}
    </div>
  );
}

/** Pages a day, saved on blur or Enter; blank clears it. */
function Membership({ club, members, meId }: { club: Club; members: Member[]; meId?: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [heir, setHeir] = useState("");
  const leaveClub = () => { qc.clear(); void navigate({ to: "/clubs", replace: true }); };
  const leave = useMutation({ mutationFn: () => api.removeMember(club.id, meId!), onSuccess: leaveClub });
  const del = useMutation({ mutationFn: () => api.deleteClub(club.id), onSuccess: leaveClub });
  const transfer = useMutation({
    mutationFn: () => api.transferClub(club.id, heir),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ["club"] }), qc.invalidateQueries({ queryKey: ["members"] }), qc.invalidateQueries({ queryKey: ["me"] })]),
  });
  const others = members.filter((m) => m.id !== meId);

  const owner = club.myRole === "owner";
  return (
    <Group title={txt.yourMembership} about={owner ? txt.youOwnThisClub : txt.leavingKeepsYourComments}>
      <MuteClub clubId={club.id} />
      <hr className="divider" />
      {owner ? (
        <>
          <div><div className="k" style={{ fontWeight: 500 }}>{txt.transferOwnership}</div><div className="muted small">{txt.youStayOnAs}</div></div>
          <div className="row">
            <select id="transfer-heir" className="input" value={heir} onChange={(e) => setHeir(e.target.value)} aria-label={txt.newOwner} disabled={others.length === 0}>
              <option value="">{others.length ? txt.chooseAMember : txt.noOtherMembersYet}</option>
              {others.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <button className="btn" disabled={!heir || transfer.isPending} onClick={() => transfer.mutate()}>{txt.transfer}</button>
          </div>
          {transfer.isError && <p className="error">{describeError(transfer.error)}</p>}
          <hr className="divider" />
          <Confirm label={txt.deleteThisClub} busy={del.isPending} onConfirm={() => del.mutate()}
            question={txt.deleteForEveryoneBooks(club.name)} />
          {del.isError && <p className="error">{describeError(del.error)}</p>}
        </>
      ) : (
        <>
          <Confirm label={txt.leaveThisClub} busy={leave.isPending} onConfirm={() => leave.mutate()}
            question={txt.yourCommentsAndProgress} />
          {leave.isError && <p className="error">{describeError(leave.error)}</p>}
        </>
      )}
    </Group>
  );
}

/**
 * A number setting, the same way as the typed fields: the value as text with
 * a pencil; the pencil opens a small input with Save and Cancel. Out-of-range
 * numbers can't be saved, and the range shows under the input. `clearable`
 * lets it be left blank (Usual pace).
 */
function NumberSetting({ id, label, value, min, max, unit, clearable, onSave }:
  { id: string; label: string; value: number | null; min: number; max: number; unit: string; clearable?: boolean; onSave: (v: number | null) => void }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value === null ? "" : String(value));
  useEffect(() => { if (!editing) setV(value === null ? "" : String(value)); }, [value, editing]);
  const n = v.trim() === "" ? null : Number(v);
  const valid = n === null ? Boolean(clearable) : Number.isInteger(n) && n >= min && n <= max;
  const can = valid && n !== value;
  const commit = () => { if (can) onSave(n); setEditing(false); };
  const cancel = () => setEditing(false);
  if (!editing) {
    return (
      <span className="nview">
        <span className="nval">{value === null ? <span className="muted">{txt.notSet}</span> : <><b className="num">{value}</b> <span className="muted">{unit}</span></>}</span>
        <button type="button" className="pencil" aria-label={txt.edit(label.toLowerCase())} onClick={() => setEditing(true)}><PencilIcon width={16} height={16} /></button>
      </span>
    );
  }
  return (
    <span className="nedit">
      <span className="row" style={{ gap: 6 }}>
        <input id={id} className="input num" type="number" inputMode="numeric" min={min} max={max} value={v} autoFocus aria-label={`${label}, ${unit}`} placeholder={clearable ? "—" : undefined}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") { e.preventDefault(); cancel(); } }} />
        <span className="muted small" style={{ whiteSpace: "nowrap" }}>{unit}</span>
        <button type="button" className="btn primary sm" disabled={!can} onClick={commit}>{txt.save}</button>
        <button type="button" className="btn ghost sm" onClick={cancel}>{txt.cancel}</button>
      </span>
      <span className={`small ${valid ? "muted" : "warn"}`}>{min}–{max}{clearable ? txt.orBlank : ""}</span>
    </span>
  );
}
